import React, { type ChangeEvent, type SyntheticEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import iconOsimage from '../../assets/os-image.svg';
import type { CloudInit, CreateInstanceProps, Keypair } from "../../lib/ModelTypes.ts";
import { instance_manager, InstanceAPIConnectorError } from "../../lib/InstanceManager.ts";
import { keypair } from "../../lib/Keypair.ts";
import { cloud_init_api } from "../../lib/CloudInit.ts";
import { swal_util } from "../../lib/SwalUtil.ts";
import { snapshot } from "../../lib/Snapshot.ts";
import { instructions } from "../../lib/Instructions.ts";
import { frontBaseUrl } from "../../build_constants.ts";
import { Navbar } from "../../components/Navbar.tsx";
import { et } from "../../locales/i18n.tsx";
import { membership } from "../../lib/Membership.ts";
import { useTranslation } from "react-i18next";
import { parseMultilingualMarkdown } from "../../util/MarkdownParser.ts";
import FlavorGPUTable from "../../components/data_table/FlavorGPUTable.tsx";

interface ContentProps {
    toggleSidebar: () => void;
}

const BmsCreateInstance = ({ toggleSidebar }: ContentProps) => {
    const { t } = useTranslation("ui");
    const { i18n } = useTranslation();

    const navigate = useNavigate();
    const fetchCalledRef = useRef(false);
    const [data, setData] = useState<CreateInstanceProps>({
        agreement: "",
        flavorData: [],
        osImageData: [],
        snapshotData: [],
        keyOptions: [],
        cloudInitOptions: [],
        loading: true,
        selectedKey: '',
        selectedCloudInit: "",
        selectedFlavor: "",
        selectedOSImage: "",
        instanceName: "",
    });

    const osImageListRef = useRef<HTMLDivElement>(null);
    const armInstanceTypeFlavorsRef = useRef<HTMLDivElement>(null);
    const instanceNameInputRef = useRef<HTMLInputElement>(null);

    async function init() {
        try {
            const instructionData = await instructions.get();
            const agreementHtml = await parseMultilingualMarkdown(
                instructionData!.data,
                i18n.language
            );
            setData(prev => ({ ...prev, agreement: agreementHtml }));
        } catch (err) {
            await swal_util.show_error(t("createInstance.errors.fetchTerms"));
            console.error(err);
        }

        try {
            await instance_manager.prepare();
            setData(prev => ({ ...prev, osImageData: instance_manager.osImages }));
            setData(prev => ({ ...prev, flavorData: instance_manager.flavors }));
            setData(prev => ({ ...prev, loading: false }));
        } catch (err) {
            await swal_util.show_error(t("createInstance.errors.fetchImagesFlavors"));
            console.error(err);
        }

        try {
            const key = await keypair.get();
            setData(prev => ({ ...prev, keyOptions: key!.data }));
            const initialDefaultId =
                key!.data.find((data: Keypair) => data.default)?.name || key!.data[0]?.name;
            setData(prev => ({ ...prev, selectedKey: initialDefaultId }));
        } catch (err) {
            await swal_util.show_error(t("createInstance.errors.fetchKeys"));
            console.error(err);
        }

        try {
            await snapshot.getSnapshotList();
            setData(prev => ({ ...prev, snapshotData: snapshot.snapshots }));
        } catch (err) {
            await swal_util.show_error(t("createInstance.errors.fetchSnapshots"));
            console.error(err);
        }

        try {
            const cloud_init = await cloud_init_api.get();
            if (cloud_init) {
                setData(prev => ({ ...prev, cloudInitOptions: cloud_init.data }));
                const initialDefaultId =
                    cloud_init.data.find((data: CloudInit) => data.default)?.name || cloud_init.data[0].name;
                setData(prev => ({ ...prev, selectedCloudInit: initialDefaultId }));
            }
        } catch (err) {
            await swal_util.show_error(t("createInstance.errors.fetchYaml"));
            console.error(err);
        }
    }

    function lazyFocus(element: HTMLElement | undefined | null, delayTime: number = 300) {
        if (!element) return;
        setTimeout(() => {
            element.scrollIntoView();
            element.focus();
        }, delayTime);
    }

    async function onClickCreateInstance() {
        if (data.instanceName.length === 0) {
            await swal_util.show_warning(t("createInstance.warn.missingVmName"));
            lazyFocus(instanceNameInputRef.current, 300);
            return false;
        }

        if (data.selectedOSImage.length === 0) {
            await swal_util.show_warning(t("createInstance.warn.selectOs"));
            lazyFocus(osImageListRef.current, 300);
            return false;
        }

        if (data.selectedFlavor === "") {
            await swal_util.show_warning(t("createInstance.warn.selectFlavor"));
            lazyFocus(armInstanceTypeFlavorsRef.current, 300);
            return false;
        }

        const snapshotPrefix = "snapshot: ";
        const [isSnapshot, osImage] = data.selectedOSImage.startsWith(snapshotPrefix)
            ? [true, data.selectedOSImage.substring(snapshotPrefix.length)]
            : [false, data.selectedOSImage];

        try {
            await instance_manager.createInstance(
                data.instanceName, data.selectedFlavor, osImage, isSnapshot, data.selectedKey, data.selectedCloudInit
            );

            navigate(`${frontBaseUrl}/bms-instance`);
        } catch (err) {
            if (err instanceof InstanceAPIConnectorError) {
                const errCause: string = err.cause.replace(/.*\./, '');

                if (err.cause.endsWith("invalid_session_id")) {
                    await swal_util.show_error(t("createInstance.errors.sessionExpired"));
                    membership.logout().finally(() => {
                        navigate(`${frontBaseUrl}/login`, { replace: true });
                    });
                } else if (errCause === "user_not_active") {
                    await swal_util.show_error(et("user_not_active"));
                } else if (errCause === 'exceeding_limit') {
                    const message: unknown | string = err.message;
                    if (message !== "string") {
                        const m = message as { amount: number };
                        await swal_util.show_error(
                            t("createInstance.errors.limitExceeded", { count: m.amount })
                        );
                    }
                } else if (errCause === 'not_exists_available_zone') {
                    await swal_util.show_error(t("createInstance.errors.availabilityZone"));
                } else if (errCause === "user_id_not_found") {
                    await swal_util.show_error(t("createInstance.errors.userIdNotFound"));
                } else if (errCause === "flavor_id_not_found") {
                    await swal_util.show_error(t("createInstance.errors.flavorNotFound"));
                } else if (errCause === "os_image_not_found") {
                    await swal_util.show_error(t("createInstance.errors.osImageNotFound"));
                }
                console.log(errCause, err);
                return;
            }

            return;
        }

        return false;
    }

    function handleSelectOSImage(rowId: string) {
        setData(prev => ({ ...prev, selectedOSImage: rowId }));
    }

    function handleSelectSnapshot(rowId: number) {
        setData(prev => ({ ...prev, selectedOSImage: "snapshot: " + rowId }));
    }

    function handleOsIconError(e: SyntheticEvent) {
        const img = e.target as HTMLImageElement;
        console.warn(`${img.dataset["name"]} icon load fail: ${img.src}`);
        img.src = iconOsimage;
    }

    function updateInstanceName(e: ChangeEvent<HTMLInputElement>) {
        setData(prev => ({ ...prev, instanceName: e.target.value }));
    }

    useEffect(() => {
        if (fetchCalledRef.current) return;
        fetchCalledRef.current = true;
        init().finally();
    }, []);

    return (
        <main className="d-block col ps-2 pt-3 min-vh-100">
            <Navbar toggleSidebar={toggleSidebar} />
            <article className="blueshift_main" id="wrapper">
                <div className="p-4 pt-2">
                    <h6 className="text-primary pt-2 col-6">{t("createInstance.title")}</h6>
                    <div className="row justify-content-center align-items-center">
                        <div>
                            <div className="form-group mx-2 my-4">
                                <p className="pt-2 mb-3 text-secondary">{t("createInstance.vmName")}</p>
                                <input
                                    type="text"
                                    className="form-control"
                                    onChange={updateInstanceName}
                                    ref={instanceNameInputRef}
                                />
                            </div>

                            <div ref={osImageListRef} className="form-group px-3 mb-4" tabIndex={0}>
                                <p className="pt-2 mb-2 text-secondary">{t("createInstance.selectOs")}</p>
                                {
                                    data.loading ? <h5 className="text-center">{t("createInstance.loadingData")}</h5> :
                                        <div className="row">
                                            {data.osImageData.map((item, index) => (
                                                <React.Fragment key={index}>
                                                    <div
                                                        tabIndex={0}
                                                        className={`col-2 text-center px-1 os-image ${data.selectedOSImage === item.id ? 'on' : ''}`}
                                                        onClick={() => handleSelectOSImage(item.id)}
                                                    >
                                                        <div className='my-2 p-1 os-image-inside'>
                                                            <img
                                                                src={item.icon_file_name ? `/static/os-icon/${item.icon_file_name}` : iconOsimage}
                                                                className="py-4"
                                                                style={{ width: '60px' }}
                                                                alt={`${item.name} icon`}
                                                                onError={(e) => handleOsIconError(e)}
                                                                data-name={item.name}
                                                            />
                                                            <p>{item.name}</p>
                                                        </div>
                                                    </div>
                                                </React.Fragment>
                                            ))}
                                            {data.snapshotData.map((item, index) => (
                                                <React.Fragment key={index}>
                                                    <div
                                                        className={`col-2 text-center px-1 os-image ${data.selectedOSImage === "snapshot: " + item.id ? 'on' : ''}`}
                                                        onClick={() => handleSelectSnapshot(item.id)}
                                                    >
                                                        <div className='my-2 p-1 os-image-inside'>
                                                            <img
                                                                src={iconOsimage}
                                                                className="py-4"
                                                                style={{ width: '60px' }}
                                                                alt={`${item.name} icon`}
                                                                onError={(e) => handleOsIconError(e)}
                                                                data-name={item.name}
                                                            />
                                                            <p>{t("createInstance.snapshot")}: {item.name}</p>
                                                        </div>
                                                    </div>
                                                </React.Fragment>
                                            ))}
                                        </div>
                                }
                            </div>

                            <div className="form-group pb-3 pt-3 border-top" tabIndex={0} ref={armInstanceTypeFlavorsRef}>
                                <p className="px-3 pt-2 mb-3 text-secondary">{t("createInstance.selectGPUFlavor")}</p>
                                <div className="table-wrapper-scroll-y my-custom-scrollbar">
                                    <FlavorGPUTable
                                        items={data.flavorData.filter(f => f.type === "BMS")}
                                        radioGroupName="gpuFlavorSelect"
                                        onChangeFlavor={(flavor) => setData(prev => ({ ...prev, selectedFlavor: flavor.id }))}
                                        data={data}
                                    />
                                </div>
                                <p><label className="px-3 mb-1 text-secondary">
                                    {t("createInstance.noteQuota")}
                                </label></p>
                            </div>

                            <br />

                            <div className="form-group px-3 mb-3 pt-3 border-top">
                                <div className="row">
                                    <div className="col-5">
                                        <p className="pt-2 mb-2 text-secondary">{t("createInstance.selectKeyPair")}</p>
                                    </div>
                                    <div className="col-7">
                                        <select
                                            name="key_pair"
                                            className="form-select"
                                            defaultValue={data.selectedKey}
                                            key={data.selectedKey}
                                            onChange={(e) => setData(prev => ({ ...prev, selectedKey: e.target.value }))}
                                        >
                                            {data.keyOptions.map((option) => (
                                                <option key={option.name} value={option.name}>
                                                    {option.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="row mt-2">
                                    <div className="col-5">
                                        <p className="pt-2 mb-2 text-secondary">{t("createInstance.selectCloudInit")}</p>
                                    </div>
                                    <div className="col-7">
                                        <select
                                            name="cloud_init"
                                            className="form-select"
                                            defaultValue={data.selectedCloudInit}
                                            key={data.selectedCloudInit}
                                            onChange={(e) =>
                                                setData(prev => ({
                                                    ...prev,
                                                    selectedCloudInit: e.target.value
                                                }))
                                            }
                                        >
                                            {data.cloudInitOptions.map((option) => (
                                                <option key={option.id} value={option.name}>
                                                    {option.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <h6 className="text-primary pt-4 col-12 text-secondary border-top">
                        {t("createInstance.termsTitle")}
                    </h6>
                    <div className="rounded-2 p-3 border border-secondary mb-5 mt-4">
                        <div
                            id="agreement_text_box"
                            dangerouslySetInnerHTML={{ __html: data.agreement }}
                        />
                    </div>

                    <div className="row justify-content-center">
                        <button
                            className="button_blue px-4 py-1 rounded-3 col-2 me-2"
                            onClick={onClickCreateInstance}
                        >
                            {t("createInstance.create")}
                        </button>
                        <Link
                            to={`${frontBaseUrl}/bms-instance`}
                            className="button_red px-4 py-1 rounded-3 col-2 text-decoration-none"
                        >
                            {t("common.cancel")}
                        </Link>
                    </div>
                </div>
            </article>
        </main>
    );
};

export default BmsCreateInstance;